// src/pages/AddInventoryItem.js

import React, { useState, useEffect, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Link,
  CircularProgress,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { addInventoryItem, getInventoryByProductId } from '../services/inventoryService';
import { getProductsByOrgId } from '../services/productService';
import { OrgContext } from '../contexts/OrgContext';
import { useTheme } from '@mui/material/styles';
import BackButton from '../components/BackButton';
import { formatDateTimeToStandard } from '../utils/DateFormatter';

const AddInventoryItem = () => {
  const { orgId, setOrgId } = useContext(OrgContext); // Ensure setOrgId is available
  const [products, setProducts] = useState([]);
  const [addedProduct, setAddedProduct] = useState(null);
  const [existingInventory, setExistingInventory] = useState(null);
  const [loading, setLoading] = useState(false); // Add loading state
  const { handleSubmit, control, setValue, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const theme = useTheme(); // Use theme for styling

  useEffect(() => {
    if (orgId) {
      getProductsByOrgId(orgId).then(setProducts);
    }
  }, [orgId]);

  const onSubmit = (data) => {
    const processedData = {
      ...data,
      org_id: orgId,
      product_id: data.product.id,
      quantity_on_hand: parseInt(data.quantity_on_hand, 10),
      minimum_stock_level: data.minimum_stock_level ? parseInt(data.minimum_stock_level, 10) : null,
      maximum_stock_level: data.maximum_stock_level ? parseInt(data.maximum_stock_level, 10) : null,
      reorder_point: data.reorder_point ? parseInt(data.reorder_point, 10) : null,
      default_order_quantity: data.default_order_quantity ? parseInt(data.default_order_quantity, 10) : null,
      last_unit_cost: data.last_unit_cost ? parseFloat(data.last_unit_cost) : null,
      last_unit_price: data.last_unit_price ? parseFloat(data.last_unit_price) : null,
    };

    setLoading(true);
    addInventoryItem(processedData)
      .then((response) => {
        setAddedProduct(response);
      })
      .catch((error) => {
        console.error('Error adding inventory item:', error);
        // Handle error
      })
      .finally(() => setLoading(false));
  };

  const handleProductChange = async (_, value) => {
    setValue('product', value);
    setLoading(true);
    if (value) {
      try {
        const inventory = await getInventoryByProductId(orgId, value.id);
        setExistingInventory(inventory);
        // Pre-fill unit cost/price from product if no existing inventory
        if (!inventory) {
          setValue('last_unit_cost', value.current_unit_cost);
          setValue('last_unit_price', value.current_unit_price);
        }
      } catch (error) {
        setExistingInventory(null);
        setValue('last_unit_cost', value.current_unit_cost);
        setValue('last_unit_price', value.current_unit_price);
      } finally {
        setLoading(false);
      }
    } else {
      setExistingInventory(null);
      setLoading(false);
    }
  };

  const renderProductDetails = (product) => {
    const formatDate = (dateString) => {
      return formatDateTimeToStandard(dateString);
    };

    const formatKey = (key) => {
      return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const renderDetails = (obj, prefix = '') => {
      return Object.entries(obj).map(([key, value]) => {
        if (value && typeof value === 'object') {
          return (
            <div key={key} style={{ marginLeft: '20px' }}>
              <Typography variant="body1">
                <strong>{prefix}{formatKey(key)}:</strong>
              </Typography>
              {renderDetails(value, `${prefix}${formatKey(key)} `)}
            </div>
          );
        }
        if (value !== null) {
          if (key === 'excess_inventory') {
            value = value ? 'Yes' : 'No';
          }
          return (
            <Typography key={key} variant="body1">
              <strong>{prefix}{formatKey(key)}:</strong> {key.includes('date') ? formatDate(value) : value}
            </Typography>
          );
        }
        return null;
      });
    };

    return renderDetails(product);
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <BackButton />
      <Typography variant="h5" gutterBottom>
        {addedProduct ? 'Product Added' : 'Add Inventory Item'}
      </Typography>
      {addedProduct ? (
        <div>
          {renderProductDetails(addedProduct)}
          <Button variant="contained" color="primary" onClick={() => setAddedProduct(null)} sx={{ mt: 2, mr: 2 }}>
            Add More Inventory
          </Button>
          <Button variant="contained" color="secondary" onClick={() => navigate(`/${orgId}/inventory`)} sx={{ mt: 2 }}>
            Inventory List
          </Button>
        </div>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="product"
                control={control}
                rules={{ required: 'Product is required' }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={products}
                    getOptionLabel={(option) => option.product_name}
                    onChange={handleProductChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!errors.product}
                        helperText={errors.product ? errors.product.message : ''}
                        fullWidth
                      />
                    )}
                  />
                )}
              />
              <Link
                href={`/${orgId}/products/add`}
                onClick={() => setOrgId(orgId)} // Ensure orgId is set in context
                sx={{ mt: 1, display: 'block', color: theme.palette.primary.light }}
              >
                Add Product
              </Link>
            </Grid>
            {loading ? (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Controller
                    name="quantity_on_hand"
                    control={control}
                    rules={{ required: 'Quantity on hand is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Quantity on Hand"
                        type="number"
                        error={!!errors.quantity_on_hand}
                        helperText={errors.quantity_on_hand ? errors.quantity_on_hand.message : ''}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {!existingInventory && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="default_order_quantity"
                        control={control}
                        rules={{ required: 'Default order quantity is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Default Order Quantity"
                            type="number"
                            error={!!errors.default_order_quantity}
                            helperText={errors.default_order_quantity ? errors.default_order_quantity.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="last_unit_cost"
                        control={control}
                        rules={{ required: !existingInventory ? 'Last unit cost is required' : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Last Unit Cost"
                            type="number"
                            step="0.01"
                            error={!!errors.last_unit_cost}
                            helperText={errors.last_unit_cost ? errors.last_unit_cost.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="last_unit_price"
                        control={control}
                        rules={{ required: !existingInventory ? 'Last unit price is required' : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Last Unit Price"
                            type="number"
                            step="0.01"
                            error={!!errors.last_unit_price}
                            helperText={errors.last_unit_price ? errors.last_unit_price.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="minimum_stock_level"
                        control={control}
                        rules={{ required: 'Minimum stock level is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Minimum Stock Level"
                            type="number"
                            error={!!errors.minimum_stock_level}
                            helperText={errors.minimum_stock_level ? errors.minimum_stock_level.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="maximum_stock_level"
                        control={control}
                        rules={{ required: 'Maximum stock level is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Maximum Stock Level"
                            type="number"
                            error={!!errors.maximum_stock_level}
                            helperText={errors.maximum_stock_level ? errors.maximum_stock_level.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="reorder_point"
                        control={control}
                        rules={{ required: 'Reorder point is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Reorder Point"
                            type="number"
                            error={!!errors.reorder_point}
                            helperText={errors.reorder_point ? errors.reorder_point.message : ''}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} textAlign="right">
                  <Button type="submit" variant="contained" color="primary">
                    Add Inventory Item
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      )}
    </Paper>
  );
};

export default AddInventoryItem;
